<template>
   <a
      v-if="isExternal"
      :href="urlBasedOnAuth"
      class="inline-block text-center"
      :class="classFromProps"
      :target="target"
   >
      <slot v-if="!text"></slot>{{ text }}
   </a>

   <router-link v-else class="inline-block text-center" :class="classFromProps" :to="urlBasedOnAuth" :target="target">
      <slot v-if="!text"></slot>{{ text }}
   </router-link>
</template>

<script setup lang="ts">
import { getButtonClass } from "./button.composable";
import { useRouteLocale } from "@/composables/useRouteLocale";
import { useAuthStore } from "@/stores/authStore";

import { Theme } from "@/types";
import { type ButtonStyling } from "./button-types";

// this is a replica of LinkElementData interface (we cant import it because of vue limitation)
interface LinkButtonCTAData {
   type?: "link";
   text?: string; // can be passed in via slot
   url: string; // url on logout/login
   urlOnLogin?: string; // url when logged in
   hideOn?: "logged_in" | "logged_out";
   target?: "_self" | "_blank" | null;
   isExternal?: boolean;
   localeId?: string | null; // locale ID to override the set session locale in the link
}

interface LinkButtonProp extends ButtonStyling, LinkButtonCTAData {}

export type { LinkButtonCTAData, LinkButtonProp };

const authStore = useAuthStore();

const props = withDefaults(defineProps<LinkButtonProp>(), {
   // link defaults
   type: "link",
   target: "_self",
   isExternal: false,
   // styling defaults
   size: "md",
   roundedEdges: "full",
   isDisabled: false,
   color: Theme.BrandDark,
   styleType: "filled",
   localeId: null,
});

const { localePath } = useRouteLocale(props.localeId);

// computed
// -----------------------------------------
const urlBasedOnAuth = computed(() => {
   if (!props || !authStore) return null;

   const url = authStore.authTokenExists && props.urlOnLogin ? props.urlOnLogin : props.url;

   if (!props.isExternal && !url.startsWith("mailto")) {
      // ex: localepath= "/en-gb" + props.url = "/account"
      const optionalSlash = url.startsWith("/") ? "" : "/";
      return `${localePath.value}${optionalSlash}${url}`;
   }
   return url;
});

const classFromProps = computed(() => {
   if (!props) return null;

   return getButtonClass(props);
});
</script>

<style scoped lang="scss">
@use "./button.scss";
</style>
